import * as i1 from '@angular/common';
import { CommonModule } from '@angular/common';
import * as i0 from '@angular/core';
import { EventEmitter, inject, forwardRef, ViewChild, Output, Input, ViewEncapsulation, Component, NgModule } from '@angular/core';
import * as i2 from '@angular/forms';
import { UntypedFormGroup, Validators, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import * as i3 from '@engineering11/ui-lib/e11-autocomplete';
import { E11AutocompleteModule } from '@engineering11/ui-lib/e11-autocomplete';
import * as i6 from '@engineering11/ui-lib/e11-directives';
import { E11DirectivesModule } from '@engineering11/ui-lib/e11-directives';
import * as i4 from '@engineering11/ui-lib/e11-inline-footer';
import { E11InlineFooterModule } from '@engineering11/ui-lib/e11-inline-footer';
import * as i5 from '@engineering11/ui-lib/e11-inline-label';
import { E11InlineLabelModule } from '@engineering11/ui-lib/e11-inline-label';
import { E11TooltipModule } from '@engineering11/ui-lib/e11-tooltip';
import { IS_BROWSER, E11UtilModule } from '@engineering11/ui-lib/e11-ui-utils';

const autocompleteSizeMap = {
    sm: 'text-sm',
    md: 'text-md',
    lg: 'text-lg',
    xl: 'text-xl',
};
class E11InlineAutocompleteComponent {
    set value(value) {
        this._value = value;
        this.displayValue = this.buildDisplayValue(value);
        this.persistValue();
    }
    constructor(cd) {
        this.cd = cd;
        this.loading = false;
        this.editing = false;
        this.required = false;
        this.editIcon = false;
        this.hasError = false;
        this.size = 'md';
        this.formControlName = '';
        this.parentForm = new UntypedFormGroup({});
        this.useSearchKeyAsItemKey = false;
        this.inputSaved = new EventEmitter();
        this.inputChange = new EventEmitter();
        this.selectedInput = new EventEmitter();
        this.inputValue = '';
        this.onChange = () => { };
        this.onTouch = () => { };
        this.invalidOption = false;
        this.disabledClick = false;
        this.optionSelected = false;
        this.labelClicked = false;
        this.isBrowser = inject(IS_BROWSER);
        this.autoCompleteSizeMap = autocompleteSizeMap;
        this.Validators = Validators;
    }
    ngOnInit() {
        this.initFormMethods();
    }
    get f() {
        return this.parentForm.controls;
    }
    initFormMethods() {
        if (!this.formControlName || !this.parentForm)
            return;
        this.formControl = this.f[this.formControlName];
        if (!this.formControl)
            return;
        if (this.parentForm.dirty && this.formControl?.errors)
            this.hasError = true;
        if (this.required && !this.formControl.hasValidator(Validators.required))
            this.formControl.addValidators([Validators.required]);
        if (this._value) {
            this.displayValue = this.buildDisplayValue(this._value);
            this.persistValue();
            this.updateFormValue(this._value);
        }
        this.formControl.updateValueAndValidity();
    }
    checkError(checkOption) {
        if (this.formControl.errors && (this.formControl.touched || this.formControl.dirty)) {
            this.hasError = true;
        }
        else {
            this.hasError = false;
        }
        this.checkValidOption(checkOption ?? this.autoInputElement?.searchInput?.nativeElement.value);
    }
    handleClickOutside() {
        if (this.optionSelected) {
            this.optionSelected = false;
            return;
        }
        if (this.labelClicked) {
            this.labelClicked = false;
            return;
        }
        this.cancelEdit();
    }
    inputSelected(value) {
        this.optionSelected = true;
        this.updateFormValue(value);
        this.selectedInput.emit(value);
    }
    saveEdit() {
        if (this.hasError || this.invalidOption) {
            this.disabledClick = true;
            return;
        }
        this._value = this.savedValue = this.f[this.formControlName].value;
        if (this._value === '')
            this._value = undefined;
        this.updateFormValue(this._value);
        this.displayValue = this.buildDisplayValue(this._value);
        this.inputSaved.emit(this._value);
        if (this.onChange) {
            this.onChange(this._value);
            this.onTouch();
        }
        this.toggleEditing();
    }
    cancelEdit() {
        this.updateFormValue(this.savedValue);
        this.toggleEditing();
    }
    toggleEditing(labelClicked = false) {
        if (labelClicked)
            this.labelClicked = true;
        this.editing = !this.editing;
        this.cd.detectChanges();
        this.editing ? this.setFocus() : this.f[this.formControlName].markAsPristine();
    }
    buildDisplayValue(value) {
        if (!value)
            return undefined;
        if (this.displayValueFn !== undefined) {
            return this.displayValueFn(value);
        }
        else {
            return value && this.autocompleteConfig?.searchKeyword ? value[this.autocompleteConfig.searchKeyword] : value;
        }
    }
    checkValidOption(value) {
        if (value === '' && !this.required) {
            this.invalidOption = false;
            return;
        }
        if (this.autocompleteConfig.searchKeyword) {
            this.invalidOption =
                this.autocompleteConfig.data.find((data) => {
                    return data[this.autocompleteConfig.searchKeyword] === value;
                }) == undefined;
        }
        else {
            this.invalidOption = !this.autocompleteConfig.data.includes(value);
        }
    }
    writeValue(obj) {
        this.inputValue = obj ? obj : '';
    }
    registerOnChange(fn) {
        this.onChange = fn;
    }
    registerOnTouched(fn) {
        this.onTouch = fn;
    }
    setFocus() {
        if (!this.isBrowser)
            return null;
        setTimeout(() => {
            this.autoInputElement.focus();
        });
    }
    handleOnChange(event) {
        let updatedValue = event.target.value.trim();
        this.updateFormValue(updatedValue);
    }
    updateFormValue(formValue) {
        if (this.formControlName && this.f[this.formControlName]) {
            this.f[this.formControlName].setValue(formValue);
        }
        this.f[this.formControlName].updateValueAndValidity();
        this.checkError(formValue && this.autocompleteConfig.searchKeyword ? formValue[this.autocompleteConfig.searchKeyword] : formValue);
    }
    persistValue() {
        if (this.formControlName && !this.f[this.formControlName]?.dirty) {
            this.savedValue = this.f[this.formControlName].value;
        }
    }
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineAutocompleteComponent, deps: [{ token: i0.ChangeDetectorRef }], target: i0.ɵɵFactoryTarget.Component }); }
    static { this.ɵcmp = i0.ɵɵngDeclareComponent({ minVersion: "14.0.0", version: "18.2.13", type: E11InlineAutocompleteComponent, selector: "e11-inline-autocomplete", inputs: { label: "label", value: "value", placeholder: "placeholder", loading: "loading", editing: "editing", required: "required", editIcon: "editIcon", hasError: "hasError", size: "size", tooltipConfig: "tooltipConfig", requiredTooltipConfig: "requiredTooltipConfig", formControlName: "formControlName", parentForm: "parentForm", autocompleteConfig: "autocompleteConfig", itemTemplate: "itemTemplate", notFoundTemplate: "notFoundTemplate", displayValueFn: "displayValueFn", useSearchKeyAsItemKey: "useSearchKeyAsItemKey" }, outputs: { inputSaved: "inputSaved", inputChange: "inputChange", selectedInput: "selectedInput" }, providers: [
            {
                provide: NG_VALUE_ACCESSOR,
                useExisting: forwardRef(() => E11InlineAutocompleteComponent),
                multi: true,
            },
        ], viewQueries: [{ propertyName: "autoInputElement", first: true, predicate: ["input"], descendants: true }], ngImport: i0, template: "<div [formGroup]=\"parentForm\">\n  <ng-container *ngIf=\"!editing\">\n    <e11-inline-label\n      [label]=\"label\"\n      [value]=\"displayValue\"\n      [placeholder]=\"placeholder\"\n      [loading]=\"loading\"\n      [size]=\"size\"\n      [hasError]=\"required ? f[formControlName].errors?.required : undefined\"\n      [required]=\"required\"\n      [editIcon]=\"true\"\n      [tooltipConfig]=\"tooltipConfig\"\n      [requiredTooltipConfig]=\"requiredTooltipConfig\"\n      (labelClicked)=\"toggleEditing(true)\"\n    ></e11-inline-label>\n  </ng-container>\n  <div (closeOnClickOutside)=\"handleClickOutside()\" *ngIf=\"editing\" class=\"e11-w-full\">\n    <div class=\"e11-mb-1\" (keyup)=\"checkError()\">\n      <div class=\"{{ autoCompleteSizeMap[size] }}\" [ngClass]=\"{ 'input-error': hasError }\">\n        <e11-autocomplete\n          #input\n          [name]=\"autocompleteConfig.name\"\n          [label]=\"autocompleteConfig.label\"\n          [placeholder]=\"autocompleteConfig.placeholder ?? placeholder ?? ''\"\n          [heading]=\"autocompleteConfig.heading\"\n          [required]=\"autocompleteConfig.required ?? required\"\n          [initialValue]=\"autocompleteConfig.initialValue\"\n          [data]=\"autocompleteConfig.data\"\n          [selectedData]=\"autocompleteConfig.selectedData ?? []\"\n          [searchKeyword]=\"autocompleteConfig.searchKeyword\"\n          [isMultiSelect]=\"autocompleteConfig.isMultiSelect\"\n          [notFoundText]=\"autocompleteConfig.notFoundText\"\n          [isLoading]=\"autocompleteConfig.isLoading\"\n          [skeletonLoading]=\"autocompleteConfig.skeletonLoading\"\n          [debounceTime]=\"autocompleteConfig.debounceTime\"\n          [minQueryLength]=\"autocompleteConfig.minQueryLength ?? 1\"\n          [focusFirst]=\"autocompleteConfig.focusFirst\"\n          [customFilter]=\"autocompleteConfig.customFilter\"\n          [formControlName]=\"formControlName\"\n          [parentForm]=\"parentForm\"\n          [hasError]=\"hasError\"\n          [itemTemplate]=\"itemTemplate ?? defaultItemTemplate\"\n          [notFoundTemplate]=\"notFoundTemplate ?? defaultNotFoundTemplate\"\n          [stopSelectEvent]=\"true\"\n          (selected)=\"inputSelected($event)\"\n          (keydown.enter)=\"saveEdit()\"\n          (inputCleared)=\"this.setFocus()\"\n          (inputChanged)=\"inputChange.emit($event)\"\n        ></e11-autocomplete>\n        <ng-template #defaultItemTemplate let-item>\n          <a [innerHTML]=\"useSearchKeyAsItemKey ? item[autocompleteConfig.searchKeyword] : item\"></a>\n        </ng-template>\n        <ng-template #defaultNotFoundTemplate let-notFound>\n          <div [innerHTML]=\"notFound\"></div>\n        </ng-template>\n      </div>\n    </div>\n    <div class=\"e11-flex e11-justify-end\">\n      <div class=\"e11-absolute\">\n        <e11-inline-footer [confirmDisabled]=\"hasError || invalidOption\" (confirm)=\"saveEdit()\" (cancel)=\"cancelEdit()\"></e11-inline-footer>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".e11-autocomplete-container>.input-container input{padding:0rem .25rem!important;height:auto!important;background:transparent;align-items:center;--tw-border-opacity: 1;border-color:rgba(var(--color-app-borders),var(--tw-border-opacity, 1))}.input-error .e11-autocomplete-container>.input-container input{--tw-border-opacity: 1;border-color:rgba(var(--color-error),var(--tw-border-opacity, 1));--tw-text-opacity: 1;color:rgba(var(--color-error),var(--tw-text-opacity, 1))}.input-error .e11-autocomplete-container>.input-container input:focus{--tw-border-opacity: 1;border-color:rgba(var(--color-error),var(--tw-border-opacity, 1));--tw-ring-opacity: 1;--tw-ring-color: rgba(var(--color-error), var(--tw-ring-opacity, 1))}.text-sm .e11-autocomplete-container>.input-container input{font-size:.875rem;line-height:1.25rem}.text-md .e11-autocomplete-container>.input-container input{font-size:1rem;line-height:1.5rem}.text-lg .e11-autocomplete-container>.input-container input{font-family:var(--font-secondary);font-size:20px;font-weight:700}\n"], dependencies: [{ kind: "directive", type: i1.NgClass, selector: "[ngClass]", inputs: ["class", "ngClass"] }, { kind: "directive", type: i1.NgIf, selector: "[ngIf]", inputs: ["ngIf", "ngIfThen", "ngIfElse"] }, { kind: "directive", type: i2.NgControlStatus, selector: "[formControlName],[ngModel],[formControl]" }, { kind: "directive", type: i2.NgControlStatusGroup, selector: "[formGroupName],[formArrayName],[ngModelGroup],[formGroup],form:not([ngNoForm]),[ngForm]" }, { kind: "directive", type: i2.RequiredValidator, selector: ":not([type=checkbox])[required][formControlName],:not([type=checkbox])[required][formControl],:not([type=checkbox])[required][ngModel]", inputs: ["required"] }, { kind: "directive", type: i2.FormGroupDirective, selector: "[formGroup]", inputs: ["formGroup"], outputs: ["ngSubmit"], exportAs: ["ngForm"] }, { kind: "directive", type: i2.FormControlName, selector: "[formControlName]", inputs: ["formControlName", "disabled", "ngModel"], outputs: ["ngModelChange"] }, { kind: "component", type: i3.E11AutocompleteComponent, selector: "e11-autocomplete", inputs: ["name", "label", "placeholder", "heading", "required", "initialValue", "data", "selectedData", "searchKeyword", "isMultiSelect", "notFoundText", "isLoading", "skeletonLoading", "debounceTime", "disabled", "minQueryLength", "focusFirst", "stopSelectEvent", "customFilter", "formControlName", "parentForm", "hasError", "itemTemplate", "notFoundTemplate"], outputs: ["selected", "inputChanged", "inputFocused", "inputCleared", "opened", "closed", "scrolledToEnd"] }, { kind: "component", type: i4.E11InlineFooterComponent, selector: "e11-inline-footer", inputs: ["confirmIcon", "cancelIcon", "confirmDisabled"], outputs: ["confirm", "cancel"] }, { kind: "component", type: i5.E11InlineLabelComponent, selector: "e11-inline-label", inputs: ["label", "value", "placeholder", "size", "loading", "hasError", "required", "editIcon", "tooltipConfig", "requiredTooltipConfig"], outputs: ["labelClicked"] }, { kind: "directive", type: i6.ClickOutsideDirective, selector: "[closeOnClickOutside]", outputs: ["closeOnClickOutside"] }], encapsulation: i0.ViewEncapsulation.None }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineAutocompleteComponent, decorators: [{
            type: Component,
            args: [{ selector: 'e11-inline-autocomplete', providers: [
                        {
                            provide: NG_VALUE_ACCESSOR,
                            useExisting: forwardRef(() => E11InlineAutocompleteComponent),
                            multi: true,
                        },
                    ], encapsulation: ViewEncapsulation.None, template: "<div [formGroup]=\"parentForm\">\n  <ng-container *ngIf=\"!editing\">\n    <e11-inline-label\n      [label]=\"label\"\n      [value]=\"displayValue\"\n      [placeholder]=\"placeholder\"\n      [loading]=\"loading\"\n      [size]=\"size\"\n      [hasError]=\"required ? f[formControlName].errors?.required : undefined\"\n      [required]=\"required\"\n      [editIcon]=\"true\"\n      [tooltipConfig]=\"tooltipConfig\"\n      [requiredTooltipConfig]=\"requiredTooltipConfig\"\n      (labelClicked)=\"toggleEditing(true)\"\n    ></e11-inline-label>\n  </ng-container>\n  <div (closeOnClickOutside)=\"handleClickOutside()\" *ngIf=\"editing\" class=\"e11-w-full\">\n    <div class=\"e11-mb-1\" (keyup)=\"checkError()\">\n      <div class=\"{{ autoCompleteSizeMap[size] }}\" [ngClass]=\"{ 'input-error': hasError }\">\n        <e11-autocomplete\n          #input\n          [name]=\"autocompleteConfig.name\"\n          [label]=\"autocompleteConfig.label\"\n          [placeholder]=\"autocompleteConfig.placeholder ?? placeholder ?? ''\"\n          [heading]=\"autocompleteConfig.heading\"\n          [required]=\"autocompleteConfig.required ?? required\"\n          [initialValue]=\"autocompleteConfig.initialValue\"\n          [data]=\"autocompleteConfig.data\"\n          [selectedData]=\"autocompleteConfig.selectedData ?? []\"\n          [searchKeyword]=\"autocompleteConfig.searchKeyword\"\n          [isMultiSelect]=\"autocompleteConfig.isMultiSelect\"\n          [notFoundText]=\"autocompleteConfig.notFoundText\"\n          [isLoading]=\"autocompleteConfig.isLoading\"\n          [skeletonLoading]=\"autocompleteConfig.skeletonLoading\"\n          [debounceTime]=\"autocompleteConfig.debounceTime\"\n          [minQueryLength]=\"autocompleteConfig.minQueryLength ?? 1\"\n          [focusFirst]=\"autocompleteConfig.focusFirst\"\n          [customFilter]=\"autocompleteConfig.customFilter\"\n          [formControlName]=\"formControlName\"\n          [parentForm]=\"parentForm\"\n          [hasError]=\"hasError\"\n          [itemTemplate]=\"itemTemplate ?? defaultItemTemplate\"\n          [notFoundTemplate]=\"notFoundTemplate ?? defaultNotFoundTemplate\"\n          [stopSelectEvent]=\"true\"\n          (selected)=\"inputSelected($event)\"\n          (keydown.enter)=\"saveEdit()\"\n          (inputCleared)=\"this.setFocus()\"\n          (inputChanged)=\"inputChange.emit($event)\"\n        ></e11-autocomplete>\n        <ng-template #defaultItemTemplate let-item>\n          <a [innerHTML]=\"useSearchKeyAsItemKey ? item[autocompleteConfig.searchKeyword] : item\"></a>\n        </ng-template>\n        <ng-template #defaultNotFoundTemplate let-notFound>\n          <div [innerHTML]=\"notFound\"></div>\n        </ng-template>\n      </div>\n    </div>\n    <div class=\"e11-flex e11-justify-end\">\n      <div class=\"e11-absolute\">\n        <e11-inline-footer [confirmDisabled]=\"hasError || invalidOption\" (confirm)=\"saveEdit()\" (cancel)=\"cancelEdit()\"></e11-inline-footer>\n      </div>\n    </div>\n  </div>\n</div>\n", styles: [".e11-autocomplete-container>.input-container input{padding:0rem .25rem!important;height:auto!important;background:transparent;align-items:center;--tw-border-opacity: 1;border-color:rgba(var(--color-app-borders),var(--tw-border-opacity, 1))}.input-error .e11-autocomplete-container>.input-container input{--tw-border-opacity: 1;border-color:rgba(var(--color-error),var(--tw-border-opacity, 1));--tw-text-opacity: 1;color:rgba(var(--color-error),var(--tw-text-opacity, 1))}.input-error .e11-autocomplete-container>.input-container input:focus{--tw-border-opacity: 1;border-color:rgba(var(--color-error),var(--tw-border-opacity, 1));--tw-ring-opacity: 1;--tw-ring-color: rgba(var(--color-error), var(--tw-ring-opacity, 1))}.text-sm .e11-autocomplete-container>.input-container input{font-size:.875rem;line-height:1.25rem}.text-md .e11-autocomplete-container>.input-container input{font-size:1rem;line-height:1.5rem}.text-lg .e11-autocomplete-container>.input-container input{font-family:var(--font-secondary);font-size:20px;font-weight:700}\n"] }]
        }], ctorParameters: () => [{ type: i0.ChangeDetectorRef }], propDecorators: { label: [{
                type: Input
            }], value: [{
                type: Input
            }], placeholder: [{
                type: Input
            }], loading: [{
                type: Input
            }], editing: [{
                type: Input
            }], required: [{
                type: Input
            }], editIcon: [{
                type: Input
            }], hasError: [{
                type: Input
            }], size: [{
                type: Input
            }], tooltipConfig: [{
                type: Input
            }], requiredTooltipConfig: [{
                type: Input
            }], formControlName: [{
                type: Input
            }], parentForm: [{
                type: Input
            }], autocompleteConfig: [{
                type: Input
            }], itemTemplate: [{
                type: Input
            }], notFoundTemplate: [{
                type: Input
            }], displayValueFn: [{
                type: Input
            }], useSearchKeyAsItemKey: [{
                type: Input
            }], inputSaved: [{
                type: Output
            }], inputChange: [{
                type: Output
            }], selectedInput: [{
                type: Output
            }], autoInputElement: [{
                type: ViewChild,
                args: ['input', { static: false }]
            }] } });

class E11InlineAutocompleteModule {
    static { this.ɵfac = i0.ɵɵngDeclareFactory({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineAutocompleteModule, deps: [], target: i0.ɵɵFactoryTarget.NgModule }); }
    static { this.ɵmod = i0.ɵɵngDeclareNgModule({ minVersion: "14.0.0", version: "18.2.13", ngImport: i0, type: E11InlineAutocompleteModule, declarations: [E11InlineAutocompleteComponent], imports: [CommonModule,
            ReactiveFormsModule,
            E11TooltipModule,
            E11AutocompleteModule,
            E11InlineFooterModule,
            E11InlineLabelModule,
            E11DirectivesModule,
            E11UtilModule], exports: [E11InlineAutocompleteComponent] }); }
    static { this.ɵinj = i0.ɵɵngDeclareInjector({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineAutocompleteModule, imports: [CommonModule,
            ReactiveFormsModule,
            E11TooltipModule,
            E11AutocompleteModule,
            E11InlineFooterModule,
            E11InlineLabelModule,
            E11DirectivesModule,
            E11UtilModule] }); }
}
i0.ɵɵngDeclareClassMetadata({ minVersion: "12.0.0", version: "18.2.13", ngImport: i0, type: E11InlineAutocompleteModule, decorators: [{
            type: NgModule,
            args: [{
                    declarations: [E11InlineAutocompleteComponent],
                    imports: [
                        CommonModule,
                        ReactiveFormsModule,
                        E11TooltipModule,
                        E11AutocompleteModule,
                        E11InlineFooterModule,
                        E11InlineLabelModule,
                        E11DirectivesModule,
                        E11UtilModule,
                    ],
                    exports: [E11InlineAutocompleteComponent],
                }]
        }] });

/**
 * Generated bundle index. Do not edit.
 */

export { E11InlineAutocompleteComponent, E11InlineAutocompleteModule };
